import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Container,
  Fade,
  FormControl,
  Grid,
  Stack,
  SxProps,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  IQuiz,
  getQuizHistory,
  loadMoreQuizHistory,
  getQuizState,
} from '../../../store/QuizSlice';
import {
  createNewSubmissionByQuizId,
  getSubmissionState,
  getSubmissionsForQuizHistory,
  loadMoreSubmissionsForQuizHistory,
  resetSubmissionListData,
} from '../../../store/SubmissionSlice';
import { getAuthState } from '../../../store/AuthSlice';
import {
  getCurriculumState,
  getCurriculums,
} from '../../../store/CurriculumSlice';
import { useAppSelector } from '../../../hooks';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import QuizListItem from './components/QuizListItem';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import SubmissionListItem from './components/SubmissionListItem';
import {
  getTopicState,
  getTopicsBySubjectData,
  resetTopicList,
} from '../../../store/TopicSlice';
import { TP_COLORS } from '../../../constants';
import { getUrlConvertedString } from '../../../helper';

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  quizFilterData: {
    limit: number;
    offset: number;
    includeSubmissionCount: number;
    curriculum?: string;
    subject?: string;
    topicId?: string;
  };
  submissionFilterData: {
    limit: number;
    offset: number;
  };
  selectedQuizItem: IQuiz | undefined;
  selectedCurriculum: TSelectOption;
  selectedSubject: TSelectOption;
  selectedTopic: TSelectOption;
  curriculumOptions: TSelectOption[];
  subjectOptions: TSelectOption[];
  topicOptions: TSelectOption[];
};

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
  ];

  options.push({
    type: 'text',
    title: 'Quiz History',
    highlight: true,
  });

  return options;
};

const allSelectOption: TSelectOption = {
  label: 'All',
  value: 'All',
};

const quizLimit = 30;
const submissionLimit = 30;
const initialState: TState = {
  quizFilterData: {
    limit: quizLimit,
    offset: 0,
    includeSubmissionCount: 1,
  },
  submissionFilterData: {
    limit: submissionLimit,
    offset: 0,
  },
  selectedQuizItem: undefined,
  selectedCurriculum: allSelectOption,
  selectedSubject: allSelectOption,
  selectedTopic: allSelectOption,
  curriculumOptions: [],
  subjectOptions: [allSelectOption],
  topicOptions: [allSelectOption],
};

const QuizCountCard = ({ count }) => {
  return (
    <Card
      elevation={0}
      style={{
        border: '1px solid #FFBE13',
        backgroundColor: 'rgba(255, 190, 19, 0.1)',
        borderRadius: '10px',
      }}
    >
      <CardContent style={{ padding: '8px' }}>
        <Stack direction="row" alignItems="center">
          <img
            src={`${process.env.PUBLIC_URL}/images/landingPage/question-mark.png`}
            alt="question-mark"
            style={{ width: '80px', height: '75px', marginRight: '10px' }}
          />
          <Stack>
            <Typography
              fontWeight={900}
              color="#FFBE13"
              sx={{ fontSize: { xs: '28px', sm: '42px' } }}
            >
              {count}
            </Typography>
            <Typography
              fontWeight={500}
              color="#475569"
              marginTop="-10px"
              sx={{ fontSize: { xs: '14px', sm: '18px' } }}
            >
              Quizzes
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const containerStyles: SxProps = {
  backgroundColor: '#E5F7F666',
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const submissionListContainerStyles: React.CSSProperties = {
  backgroundColor: '#F8FAFC',
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  border: '1px solid #E2E8F0',
  borderBottomLeftRadius: '5px',
  borderBottomRightRadius: '5px',
};

const MyQuizHistory: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const authState = useAppSelector(getAuthState);
  const quizState = useAppSelector(getQuizState);
  const submissionState = useAppSelector(getSubmissionState);
  const curriculumState = useAppSelector(getCurriculumState);
  const topicState = useAppSelector(getTopicState);

  const authUser = authState.user;
  const breadcrumbOptions = constructBreadcrumbOptions();

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isSubmissionListLoading, setSubmissionListLoading] = useState(false);

  useEffect(() => {
    dispatch(getCurriculums());

    if (authUser) {
      dispatch(
        getQuizHistory({
          ...state.quizFilterData,
          userId: authUser._id,
        })
      );
    }

    dispatch(resetSubmissionListData());
  }, []);

  useEffect(() => {
    if (
      curriculumState.list &&
      curriculumState.list.length &&
      !state.curriculumOptions.length
    ) {
      const curriculumOptions = [allSelectOption];

      curriculumState.list.forEach((item) => {
        curriculumOptions.push({
          label: item.name,
          value: item.name,
        });
      });

      setState((prevState) => ({
        ...prevState,
        curriculumOptions,
      }));
    }
  }, [curriculumState.list]);

  useEffect(() => {
    if (topicState.list && topicState.list.length) {
      const topicOptions = [allSelectOption];

      topicState.list.forEach((item) => {
        topicOptions.push({
          label: item.name,
          value: item._id,
        });
      });

      setState((prevState) => ({
        ...prevState,
        topicOptions,
      }));
    }
  }, [topicState.list]);

  const handleQuizLoadMore = () => {
    const newQuizFilterData = {
      ...state.quizFilterData,
      offset: quizState.list.length,
    };

    if (authUser) {
      dispatch(
        loadMoreQuizHistory({
          ...newQuizFilterData,
          userId: authUser._id,
        })
      );
    }

    setState((prevState) => ({
      ...prevState,
      quizFilterData: newQuizFilterData,
    }));
  };

  const handleSubmissionLoadMore = async () => {
    if (state.selectedQuizItem) {
      setSubmissionListLoading(true);

      const newSubmissionFilterData = {
        ...state.submissionFilterData,
        offset: submissionState.list.length,
      };

      setState((prevState) => ({
        ...prevState,
        submissionFilterData: newSubmissionFilterData,
      }));

      await dispatch(
        loadMoreSubmissionsForQuizHistory({
          ...newSubmissionFilterData,
          quizId: state.selectedQuizItem._id,
        })
      );

      setSubmissionListLoading(false);
    }
  };

  const onQuizListItemClicked = async (quiz: IQuiz) => {
    if (state.selectedQuizItem && quiz._id === state.selectedQuizItem._id) {
      dispatch(resetSubmissionListData());

      setState((prevState) => ({
        ...prevState,
        selectedQuizItem: undefined,
      }));
    } else {
      setSubmissionListLoading(true);
      dispatch(resetSubmissionListData());

      const newSubmissionFilterData = { ...initialState.submissionFilterData };

      setState((prevState) => ({
        ...prevState,
        selectedQuizItem: quiz,
        submissionFilterData: newSubmissionFilterData,
      }));

      await dispatch(
        getSubmissionsForQuizHistory({
          ...newSubmissionFilterData,
          quizId: quiz._id,
        })
      );

      setSubmissionListLoading(false);
    }
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption
  ) => {
    dispatch(resetTopicList());

    setState((prevState) => {
      const newStateData = {
        ...initialState,
        quizFilterData: { ...initialState.quizFilterData },
        selectedCurriculum: value,
        curriculumOptions: prevState.curriculumOptions,
      };

      if (value.value !== 'All') {
        newStateData.quizFilterData.curriculum = value.value;
      }

      if (authUser) {
        dispatch(
          getQuizHistory({
            ...newStateData.quizFilterData,
            userId: authUser._id,
          })
        );
      }

      if (
        value.value !== 'All' &&
        curriculumState.list &&
        curriculumState.list.length
      ) {
        const selectedCurriculumItem = curriculumState.list.find(
          (item) => item.name === value.value
        );

        if (
          selectedCurriculumItem &&
          selectedCurriculumItem.grades &&
          selectedCurriculumItem.grades.length
        ) {
          const newSubjectOptions: TSelectOption[] = [];

          selectedCurriculumItem.grades.forEach((gradeItem) => {
            if (gradeItem.subjects && gradeItem.subjects.length) {
              gradeItem.subjects.forEach((subjectItem) => {
                if (
                  !newSubjectOptions.some(
                    (item) => item.value === subjectItem.name
                  )
                ) {
                  newSubjectOptions.push({
                    label: subjectItem.name,
                    value: subjectItem.name,
                  });
                }
              });

              newStateData.subjectOptions = newSubjectOptions;
            }
          });
        }
      }

      return newStateData;
    });
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption
  ) => {
    dispatch(resetTopicList());

    setState((prevState) => {
      const newStateData: TState = {
        ...prevState,
        quizFilterData: {
          ...initialState.quizFilterData,
          curriculum: state.selectedCurriculum.value,
        },
        selectedSubject: value,
        selectedTopic: allSelectOption,
        topicOptions: [allSelectOption],
      };

      if (value.value !== 'All') {
        newStateData.quizFilterData.subject = value.value;

        dispatch(
          getTopicsBySubjectData({
            curriculum: state.selectedCurriculum.value,
            subject: value.value,
          })
        );
      }

      if (authUser) {
        dispatch(
          getQuizHistory({
            ...newStateData.quizFilterData,
            userId: authUser._id,
          })
        );
      }

      return newStateData;
    });
  };

  const handleTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption
  ) => {
    setState((prevState) => {
      const newStateData: TState = {
        ...prevState,
        quizFilterData: {
          ...initialState.quizFilterData,
          curriculum: state.selectedCurriculum.value,
          subject: state.selectedSubject.value,
        },
        selectedTopic: value,
      };

      if (value.value !== 'All') {
        newStateData.quizFilterData.topicId = value.value;
      }

      if (authUser) {
        dispatch(
          getQuizHistory({
            ...newStateData.quizFilterData,
            userId: authUser._id,
          })
        );
      }

      return newStateData;
    });
  };

  const renderInput = (data: {
    params: AutocompleteRenderInputParams;
    isLoading: boolean;
    placeholder?: string;
    label: string;
  }) => (
    <TextField
      {...data.params}
      placeholder={data.placeholder}
      label={data.label}
      InputProps={{
        ...data.params.InputProps,
        style: {
          backgroundColor: TP_COLORS.white,
        },
        endAdornment: (
          <>
            {data.isLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              data.params.InputProps.endAdornment
            )}
          </>
        ),
      }}
    />
  );

  const renderSubmissionList = () => {
    if (state.selectedQuizItem) {
      const _quizId = state.selectedQuizItem._id;
      const _topic = state.selectedQuizItem.topic;
      const _urlConvertedCurriculum = getUrlConvertedString(_topic.curriculum);
      const _urlConvertedSubject = getUrlConvertedString(_topic.subject || '');
      const _urlConvertedTopic = getUrlConvertedString(_topic.name || '');

      let _subTopicId = '';
      let urlConvertedSubTopic = '';

      if (
        state.selectedQuizItem.subTopics &&
        state.selectedQuizItem.subTopics.length &&
        state.selectedQuizItem.subTopics[0] &&
        state.selectedQuizItem.subTopics[0].subTopic &&
        state.selectedQuizItem.subTopics[0].subTopic._id
      ) {
        const { subTopic } = state.selectedQuizItem.subTopics[0];

        _subTopicId = subTopic._id;

        urlConvertedSubTopic = getUrlConvertedString(subTopic.name);
      }

      return (
        <Box>
          <Stack>
            {isSubmissionListLoading && !(submissionState.list || []).length ? (
              <div style={{ padding: '30px 0' }}>
                <BasicLoader />
              </div>
            ) : null}
            <Collapse
              in={
                (submissionState.list || []).length > 0 ||
                !isSubmissionListLoading
              }
            >
              <Grid container spacing={2}>
                {(submissionState.list || []).map((item, index) => {
                  const key = `${_quizId}-submission-li-${index}`;
                  const attemptNumber = (index + 1).toString().padStart(2, '0');

                  return (
                    <Grid key={key} item xs={12}>
                      <SubmissionListItem
                        submission={item}
                        attemptNumber={attemptNumber}
                        onView={() => {
                          if (item.status === 'pending') {
                            navigate(
                              `${process.env.PUBLIC_URL}/dashboard/${_urlConvertedCurriculum}/${_urlConvertedSubject}/${_urlConvertedTopic}/${urlConvertedSubTopic}/${_subTopicId}/quiz/${item._id}`
                            );
                          } else {
                            navigate(
                              `${process.env.PUBLIC_URL}/dashboard/${_urlConvertedCurriculum}/${_urlConvertedSubject}/${_urlConvertedTopic}/${urlConvertedSubTopic}/${_subTopicId}/view-quiz/${item._id}`
                            );
                          }
                        }}
                      />
                    </Grid>
                  );
                })}
                {submissionState.count > submissionState.list.length ? (
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="center">
                      <LoadingButton
                        onClick={handleSubmissionLoadMore}
                        sx={{ color: '#01A79F' }}
                        loading={
                          submissionState.status === 'loading' ||
                          isSubmissionListLoading
                        }
                      >
                        Load More
                      </LoadingButton>
                    </Stack>
                  </Grid>
                ) : null}
              </Grid>
            </Collapse>
          </Stack>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box sx={containerStyles}>
      <Container maxWidth="lg">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            showDropDown={belowMd}
            textColor="#647488"
          />
        </div>
        <div style={{ marginBottom: '30px' }}>
          <Typography fontWeight={500} fontSize="25px">
            Quiz History
          </Typography>
        </div>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={4} lg={3}>
              <QuizCountCard count={quizState.count || 0} />
            </Grid>
          </Grid>
        </div>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} lg={3}>
              <FormControl fullWidth>
                <Autocomplete
                  disableClearable
                  size="small"
                  value={state.selectedCurriculum}
                  options={state.curriculumOptions}
                  loading={curriculumState.status === 'loading'}
                  onChange={handleCurriculumChange}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: curriculumState.status === 'loading',
                      placeholder: 'Select Curriculum',
                      label: 'Curriculum',
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
              <FormControl fullWidth>
                <Autocomplete
                  disableClearable
                  size="small"
                  value={state.selectedSubject}
                  options={state.subjectOptions}
                  onChange={handleSubjectChange}
                  loading={curriculumState.status === 'loading'}
                  disabled={
                    !state.selectedCurriculum ||
                    (state.selectedCurriculum &&
                      state.selectedCurriculum.value === 'All')
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: curriculumState.status === 'loading',
                      placeholder: 'Select Subject',
                      label: 'Subject',
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  onChange={handleTopicChange}
                  options={state.topicOptions}
                  value={state.selectedTopic}
                  disabled={
                    !state.selectedCurriculum ||
                    (state.selectedCurriculum &&
                      state.selectedCurriculum.value === 'All') ||
                    !state.selectedSubject ||
                    (state.selectedSubject &&
                      state.selectedSubject.value === 'All')
                  }
                  loading={
                    topicState.status === 'loading' ||
                    curriculumState.status === 'loading'
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: topicState.status === 'loading',
                      placeholder: 'Select Topic',
                      label: 'Topic',
                    })
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
        <div style={{ paddingBottom: '15px' }}>
          {quizState.list && quizState.list.length ? (
            <Fade in>
              <Grid container spacing={2}>
                {quizState.list.map((item, index) => {
                  const isCollapseOpen =
                    state.selectedQuizItem &&
                    state.selectedQuizItem._id === item._id;

                  let subTopicId = '';

                  if (
                    item.subTopics &&
                    item.subTopics.length &&
                    item.subTopics[0].subTopic &&
                    item.subTopics[0].subTopic._id
                  ) {
                    subTopicId = item.subTopics[0].subTopic._id;
                  }

                  return (
                    <Grid key={`quiz-li-${index + 1}`} item xs={12}>
                      <QuizListItem
                        quiz={item}
                        onClick={() => onQuizListItemClicked(item)}
                        matchBotoomStyles={isCollapseOpen}
                        onTakeQuiz={() => {
                          if (subTopicId) {
                            dispatch(
                              createNewSubmissionByQuizId({
                                subTopicId,
                                quizId: item._id,
                              })
                            );
                          }
                        }}
                      />
                      {isCollapseOpen ? (
                        <Card
                          elevation={0}
                          style={submissionListContainerStyles}
                        >
                          <CardContent style={{ padding: '16px' }}>
                            {state.selectedQuizItem &&
                            state.selectedQuizItem._id === item._id
                              ? renderSubmissionList()
                              : null}
                          </CardContent>
                        </Card>
                      ) : null}
                    </Grid>
                  );
                })}
                {quizState.count > quizState.list.length ? (
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="center">
                      <LoadingButton
                        onClick={handleQuizLoadMore}
                        sx={{ color: '#01A79F' }}
                        loading={quizState.status === 'loading'}
                      >
                        Load More
                      </LoadingButton>
                    </Stack>
                  </Grid>
                ) : null}
              </Grid>
            </Fade>
          ) : null}
        </div>
      </Container>
    </Box>
  );
};

export default MyQuizHistory;
